import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckEmailDialog } from '@/components/onboarding/CheckEmailDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MemberRolesEditor, describeMemberRoles } from '@/components/roles/MemberRolesEditor';
import { WorkRoleMultiSelect } from '@/components/roles/WorkRoleMultiSelect';
import { WorkEligibilityReconciliationCard } from '@/components/roles/WorkEligibilityReconciliationCard';
import {
  resolveMembershipRoles, AUTHORITY_ROLES, AUTHORITY_LABELS,
  type AuthorityRole, type WorkRole,
} from '@/lib/roles/multiRole';
import { canInviteWithAuthority, canAdministerTeam } from '@/lib/roles/authority';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTutorial } from '@/contexts/TutorialContext';
import { UserPlus, Trash2, Shield, Users, Mail, Info } from 'lucide-react';
import { Database } from '@/integrations/supabase/types';
import { useFormFactor } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { resolveTeamPassportOptions } from '@/lib/passport/teamPassportViews';
import { TeamPassportControl } from '@/components/passport/TeamPassportControl';
import { syncTechnicianToPassport } from '@/utils/syncTechnicianToPassport';
import { pickPersonDisplayName, validatePersonName } from '@/lib/people/personDisplayName';

type AppRole = Database['public']['Enums']['app_role'];

interface TeamMember {
  id: string;
  user_id: string;
  role: AppRole;
  /** The authoritative access level (multi-role). Absent on pre-migration rows. */
  authority_role?: string | null;
  /** When somebody last STATED this membership's work-role set (null = never). */
  work_roles_initialized_at?: string | null;
  status: string;
  created_at: string;
  profiles: {
    email: string;
    display_name: string | null;
  } | null;
}

/** A member's human name — never the email stored as a signup default. */
const memberName = (m: TeamMember): string | null =>
  pickPersonDisplayName({ profileDisplayName: m.profiles?.display_name ?? null, email: m.profiles?.email ?? null });

const TeamManagement = () => {
  const { activeShop, membership, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isActive, currentStepData, markStepComplete, nextStep } = useTutorial();
  const formFactor = useFormFactor();
  const flags = useFeatureFlags();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  // ── ACCESS AND WORK ROLES, ASKED SEPARATELY ─────────────────────────────
  //
  // An invitation carries an ACCESS level and a SET of work roles, and the
  // form asks for them as two questions. The legacy `role` scalar is NOT sent
  // any more: it is a compatibility projection the server derives from these
  // two, and asking an operator to pick it was how "choose Technician so a
  // technician row appears" became a thing people had to know.
  //
  // STAFF is the default because it is the ordinary answer for shop staffing —
  // the person does a job and administers nothing.
  const [inviteAuthority, setInviteAuthority] = useState<AuthorityRole>('STAFF');
  const [inviteWorkRoles, setInviteWorkRoles] = useState<WorkRole[]>(['TECHNICIAN']);
  // WS-ELIG B-E4 (CORE-W:45): "this person is already on our roster as …".
  // Naming the entry is the shop's half of the adoption authority; the
  // person's acceptance (which discloses it) is the other. Never inferred.
  const [unclaimedRoster, setUnclaimedRoster] = useState<{ id: string; name: string }[]>([]);
  const [inviteRosterEntry, setInviteRosterEntry] = useState<string>('none');

  // Work roles for every member, keyed by membership id. Loaded alongside the
  // member list because the row summary needs them.
  const [workRolesByMembership, setWorkRolesByMembership] = useState<Record<string, WorkRole[]>>({});
  // False when the work-role read failed: every member then resolves through
  // the legacy scalar (NOT LOADED), rather than reading as "does no work".
  const [workRolesLoaded, setWorkRolesLoaded] = useState(false);
  // Oct 20 Issue 05: each member's technician_profiles.id AS THE SERVER RETURNS
  // IT TO THIS VIEWER (visible_technician_passports: self, Owner/Manager,
  // Service Advisor of the shop, ORG_ADMIN of its organisation, or the recruiting basis). One batch read for the whole list, never per row. A
  // member absent from this map has no Technician Passport this viewer may open.
  const [visibleTechProfileByUser, setVisibleTechProfileByUser] = useState<Record<string, string>>({});
  const [editingRolesFor, setEditingRolesFor] = useState<string | null>(null);
  const [savingRoles, setSavingRoles] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<string | null>(null);
  const [showRoleGuide, setShowRoleGuide] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editNameError, setEditNameError] = useState<string | null>(null);

  // THE SAME QUESTION THE SERVER ASKS.
  //
  // This was `membership?.role === 'MANAGER' || === 'OWNER'` — the LEGACY
  // SCALAR — while every endpoint behind this page admits ROLES_DESTRUCTIVE
  // (OWNER, MANAGER, ORG_ADMIN). An ORG_ADMIN was shown "access denied" by
  // the client and accepted by the server, so the UI was the only thing
  // refusing them. `canAdministerTeam` is the canonical answer, derived from
  // the capability model rather than a string list.
  const isManager = canAdministerTeam(membership as never);
  // The viewer's resolved authority — what the Advisor / Manager Passport pages
  // themselves check (isElevatedAuthority over the canonical resolver).
  const viewerAuthority = resolveMembershipRoles(membership as never).authority;

  /**
   * ONLY OFFER WHAT THIS ACTOR MAY ACTUALLY CONFER.
   *
   * A manager sees Staff and nothing else, because minting MANAGER /
   * ORG_ADMIN / OWNER requires `manage_authority`. This is a courtesy so the
   * form does not present a choice that will bounce — `send-team-invitation`
   * re-decides it server-side, and a tampered payload is refused there.
   */
  const invitableAuthorities = useMemo<AuthorityRole[]>(() => {
    if (!membership || !activeShop) return ['STAFF'];
    return AUTHORITY_ROLES.filter((r) =>
      canInviteWithAuthority(membership as never, r, { shopId: activeShop.id }));
  }, [membership, activeShop]);

  // A manager who had picked something they may not grant (or a stale default)
  // is snapped back to the floor rather than silently sending a refused value.
  useEffect(() => {
    if (!invitableAuthorities.includes(inviteAuthority)) {
      setInviteAuthority(invitableAuthorities[0] ?? 'STAFF');
    }
  }, [invitableAuthorities, inviteAuthority]);

  useEffect(() => {
    if (activeShop) {
      loadTeamMembers();
    }
  }, [activeShop]);

  const loadTeamMembers = async () => {
    if (!activeShop) return;

    try {
      // `*` (as AuthContext reads it) so the authoritative `authority_role`
      // and `work_roles_initialized_at` arrive with the row. Reading only the
      // legacy `role` here is what made the row summary and the Passport links
      // describe a multi-role person by one collapsed scalar.
      const { data: rawData, error } = await supabase
        .from('memberships')
        .select('*')
        .eq('shop_id', activeShop.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const data = (rawData ?? []) as unknown as Omit<TeamMember, 'profiles'>[];

      // Fetch profile data separately
      if (data.length > 0) {
        const userIds = data.map(m => m.user_id);
        // Three independent batch reads — one query each, never one per row.
        const [{ data: profiles }, roleRead, { data: techProfiles }] = await Promise.all([
          supabase
            .from('profiles')
            .select('id, email, display_name')
            .in('id', userIds),
          // Work roles live in a child table, so the row summary and the
          // editor both need them loaded here rather than per-row.
          supabase
            .from('membership_work_roles' as never)
            .select('membership_id, work_role')
            .in('membership_id', data.map((m) => m.id)),
          // Read-only: the server answers which Technician Passports THIS viewer
          // may open (Oct 20 addendum P1/P2 — self, Owner/Manager, same-shop
          // Service Advisor, ORG_ADMIN of the shop's organisation). Ids only;
          // nothing is created for anyone who has none.
          supabase.rpc('visible_technician_passports' as never, { p_user_ids: userIds } as never),
        ]);

        const membersWithProfiles = data.map(membership => ({
          ...membership,
          profiles: profiles?.find(p => p.id === membership.user_id) || null,
        }));

        const byMembership: Record<string, WorkRole[]> = {};
        for (const r of ((roleRead as { data: unknown }).data ?? []) as { membership_id: string; work_role: WorkRole }[]) {
          (byMembership[r.membership_id] ??= []).push(r.work_role);
        }

        const profileByUser: Record<string, string> = {};
        for (const tp of [...((techProfiles ?? []) as unknown as { technician_profile_id: string; user_id: string | null }[])]
          .sort((a, b) => a.technician_profile_id.localeCompare(b.technician_profile_id))) {
          if (tp.user_id && !profileByUser[tp.user_id]) profileByUser[tp.user_id] = tp.technician_profile_id;
        }

        setMembers(membersWithProfiles as TeamMember[]);
        setWorkRolesByMembership(byMembership);
        setWorkRolesLoaded(!(roleRead as { error: unknown }).error);
        setVisibleTechProfileByUser(profileByUser);
      } else {
        setMembers([]);
        setWorkRolesByMembership({});
        setWorkRolesLoaded(true);
        setVisibleTechProfileByUser({});
      }
    } catch (error) {
      console.error('Error loading team members:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load team members',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // WS-ELIG B-E4: roster entries nobody has joined as yet (no bound account).
  useEffect(() => {
    if (!isInviteOpen || !activeShop?.id) return;
    let cancelled = false;
    (async () => {
      const [{ data: techs }, { data: bound }] = await Promise.all([
        supabase.from('technicians').select('id, name').eq('shop_id', activeShop.id).eq('employment_status', 'Active').order('name'),
        supabase.from('technician_users').select('technician_id').eq('shop_id', activeShop.id),
      ]);
      if (cancelled) return;
      const boundIds = new Set(((bound ?? []) as { technician_id: string }[]).map((b) => b.technician_id));
      setUnclaimedRoster(((techs ?? []) as { id: string; name: string }[]).filter((t) => !boundIds.has(t.id)));
    })();
    return () => { cancelled = true; };
  }, [isInviteOpen, activeShop?.id]);

  const handleInvite = async () => {
    if (!activeShop || !inviteEmail) return;

    try {
      // TWO INDEPENDENT HALVES, AND NO LEGACY SCALAR.
      //
      // `role` is deliberately not sent. The server derives the compatibility
      // scalar from the authority and work roles it has actually authorized,
      // so there is no third field for a refused value to travel in.
      const { error } = await supabase.functions.invoke('send-team-invitation', {
        body: {
          shopId: activeShop.id,
          email: inviteEmail,
          authorityRole: inviteAuthority,
          workRoles: inviteWorkRoles,
          shopName: activeShop.name,
          ...(inviteRosterEntry !== 'none' ? { technicianId: inviteRosterEntry } : {}),
        },
      });

      if (error) throw error;

      toast({
        title: 'Invitation sent',
        description: `An invitation email has been sent to ${inviteEmail}`,
      });

      setIsInviteOpen(false);
      setInviteEmail('');
      setInviteAuthority(invitableAuthorities[0] ?? 'STAFF');
      setInviteWorkRoles(['TECHNICIAN']);
      setInviteRosterEntry('none');

      // If in tutorial at 'invite-member', advance to 'check-email' step
      if (isActive && currentStepData?.id === 'invite-member') {
        try { await markStepComplete('invite-member'); } catch { /* best-effort; ignore */ }
        nextStep();
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to send invitation. Please try again.',
      });
    }
  };

  /**
   * Save ACCESS and WORK ROLES together, through the server.
   *
   * The old `handleUpdateRole` below turned one role INTO another, which is
   * what made "owner who also turns wrenches" unrepresentable. This sends the
   * desired work-role SET, so adding or removing one leaves the others alone,
   * and the edge function re-checks authority, scope and self-elevation
   * regardless of what the client believed.
   */
  const handleSaveRoles = async (
    member: TeamMember,
    next: { authorityRole?: AuthorityRole; workRoles?: WorkRole[] },
  ) => {
    if (!activeShop) return;

    // AN OMITTED FIELD IS NOT A MUTATION.
    //
    // This used to send `authority_role` and `work_roles` on every save,
    // whatever the editor had changed. The server treats a field that is
    // PRESENT as a request to change it, so a manager — who may edit work
    // roles and not authority — had every save refused with
    // `manage_authority required`, and the work-role controls they were
    // correctly shown could never actually be saved.
    //
    // The editor now hands over only the halves it changed, and they are
    // forwarded only if present. The server is unchanged and still decides.
    const body: Record<string, unknown> = {
      shop_id: activeShop.id,
      target_user_id: member.user_id,
    };
    if (next.authorityRole !== undefined) body.authority_role = next.authorityRole;
    if (next.workRoles !== undefined) body.work_roles = next.workRoles;

    if (body.authority_role === undefined && body.work_roles === undefined) {
      setEditingRolesFor(null);
      return;
    }

    setSavingRoles(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-membership-roles', {
        body,
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));

      // The server returns the ACTUAL post-mutation set, which is what the
      // person should be told about — an authority-only save leaves the work
      // roles alone, and reporting them from the request would describe a set
      // this save never touched.
      const applied = (data?.work_roles ?? []) as string[];
      toast({
        title: 'Roles updated',
        description: `${memberName(member) ?? 'This member'} — ${
          applied.length ? applied.length + ' work role(s)' : 'no work roles'
        }.`,
      });
      setEditingRolesFor(null);
      await loadTeamMembers();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update roles';
      toast({
        variant: 'destructive',
        title: 'Could not update roles',
        // The server's refusal reasons are deliberately specific.
        description:
          message === 'self_elevation_refused' ? 'You cannot raise your own access level.'
          : message === 'cannot_grant_unheld_capability' ? 'You cannot grant access you do not hold yourself.'
          : message === 'forbidden' ? 'You do not have permission to make that change.'
          : message,
      });
    } finally {
      setSavingRoles(false);
    }
  };

  const handleUpdateRole = async (membershipId: string, newRole: AppRole) => {
    if (!isManager || !activeShop) return;

    const member = members.find(m => m.id === membershipId);
    if (!member) return;

    try {
      const { data, error } = await supabase.functions.invoke('change-member-role', {
        body: {
          shop_id: activeShop.id,
          target_user_id: member.user_id,
          new_role: newRole,
          reason: 'Changed via Team Management',
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Role updated',
        description: 'Team member role has been updated successfully',
      });

      loadTeamMembers();
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to update role',
      });
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete || !isManager || !activeShop) return;

    const member = members.find(m => m.id === memberToDelete);
    if (!member) return;

    try {
      const { data, error } = await supabase.functions.invoke('remove-member-from-shop', {
        body: {
          shop_id: activeShop.id,
          target_user_id: member.user_id,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Member removed',
        description: 'Team member has been removed from the shop.',
      });

      loadTeamMembers();
      setMemberToDelete(null);
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to remove team member',
      });
    }
  };

  /**
   * The Access + Work Roles summary and its editor entry point.
   *
   * Shared by the phone card list and the table row so there is ONE place that
   * decides what a member's roles say and ONE button that opens the editor.
   * Duplicating either would let the two presentations drift apart, and the
   * whole point of the card view is that it is the same surface laid out for a
   * thumb.
   */
  /**
   * The member's roles in the shape the canonical resolver reads. Rows present
   * → that set. No rows → `[]` when somebody STATED the set (it is empty), or
   * `null` (not stated / not loaded) so the legacy scalar answers — the one
   * documented compatibility rule in multiRole.ts.
   */
  const memberRoleInput = (member: TeamMember) => ({
    role: member.role,
    authority_role: member.authority_role ?? null,
    work_roles: workRolesByMembership[member.id]
      ?? (workRolesLoaded && member.work_roles_initialized_at ? [] : null),
    work_roles_initialized_at: member.work_roles_initialized_at ?? null,
  });

  const renderRoleSummary = (member: TeamMember) => (
    <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:gap-2">
      <span
        className="text-sm text-muted-foreground"
        data-testid={`member-roles-summary-${member.user_id}`}
      >
        {describeMemberRoles(memberRoleInput(member))}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="w-full sm:w-auto"
        disabled={!isManager}
        onClick={() => setEditingRolesFor(member.id)}
        data-testid={`member-edit-roles-${member.user_id}`}
      >
        Edit roles
      </Button>
    </div>
  );

  /** The per-member action cluster, shared by the card and the table row. */
  const renderMemberActions = (member: TeamMember) => (
    <>
      {/* ONE Passport control per employee (Oct 20 Issue 05 / FD-08).
          Views come from the person's authoritative roles (authority_role +
          work roles), never the legacy `role` scalar; the technician view is
          enabled only for a profile the server returned to this viewer. The
          advisor/manager views are gated by the per-shop feature flags
          (true in CORE_PRESET), not by a subscription tier. */}
      <TeamPassportControl
        memberUserId={member.user_id}
        memberName={memberName(member)}
        options={resolveTeamPassportOptions({
          member: { ...memberRoleInput(member), user_id: member.user_id },
          viewerUserId: user?.id,
          viewerAuthority,
          visibleTechnicianProfileId: visibleTechProfileByUser[member.user_id] ?? null,
          flags: { advisorPassports: !!flags.advisorPassports, managerPassports: !!flags.managerPassports },
        })}
        onOpen={(href) => navigate(href)}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleEditMember(member)}
        disabled={!isManager}
        data-testid={`edit-member-${member.id}`}
        className={member.role === 'TECHNICIAN' ? 'edit-tech-button' : ''}
      >
        Edit
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setMemberToDelete(member.id)}
        disabled={!isManager}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </>
  );

  const handleEditMember = (member: TeamMember) => {
    setEditingMember(member);
    // Pre-fill only a REAL name. Signup stores the email as display_name when
    // no name was given; pre-filling it made "Save" persist the email as the
    // person's name.
    setEditName(memberName(member) ?? '');
    setEditEmail(member.profiles?.email || '');
    setEditNameError(null);
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;
    const nameError = validatePersonName(editName, editingMember.profiles?.email);
    setEditNameError(nameError);
    if (nameError) return;
    const name = editName.trim();

    try {
      // The person's one canonical human name (profiles.display_name — one
      // per person, across shops). Owners/managers may write it for an active
      // member of their shop (RLS "Managers and owners can update team member
      // profiles").
      const { data: updatedProfiles, error: profileError } = await supabase
        .from('profiles')
        .update({
          display_name: name,
        })
        .eq('id', editingMember.user_id)
        .select('id');

      if (profileError) throw profileError;
      // An update the policy refuses changes nothing and reports no error.
      if (!updatedProfiles || updatedProfiles.length === 0) {
        throw new Error("You are not allowed to change this person's name.");
      }

      // Anyone bound to this shop's roster — a technician, or a manager /
      // owner who also works the floor — gets the same name on the roster, so
      // one person never carries two conflicting names.
      {
        const { data: techUser } = await supabase
          .from('technician_users')
          .select('technician_id')
          .eq('user_id', editingMember.user_id)
          .eq('shop_id', activeShop?.id)
          .maybeSingle();

        if (techUser?.technician_id) {
          const { error: techError } = await supabase
            .from('technicians')
            .update({ name })
            .eq('id', techUser.technician_id);
          
          if (techError) throw techError;

          // Sync name change to passport snapshot
          await syncTechnicianToPassport(techUser.technician_id);
        }
      }

      toast({
        title: 'Member updated',
        description: 'Team member information has been updated successfully',
      });

      // Close dialog first
      setEditingMember(null);

      // Reload team members to get fresh data
      await loadTeamMembers();

    } catch (error) {
      console.error('Error updating member:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update team member',
      });
    }
  };

  const handleEditTechnician = async (userId: string) => {
    if (!activeShop) return;

    try {
      // Find technician linked to this user
      const { data: techUser } = await supabase
        .from('technician_users')
        .select('technician_id')
        .eq('user_id', userId)
        .eq('shop_id', activeShop.id)
        .maybeSingle();

      if (techUser?.technician_id) {
        navigate(`/technicians/${techUser.technician_id}/edit`);
      } else {
        toast({
          variant: 'destructive',
          title: 'Technician Not Found',
          description: 'No technician profile found for this user',
        });
      }
    } catch (error) {
      console.error('Error finding technician:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to find technician profile',
      });
    }
  };

  const getRoleBadgeColor = (role: AppRole) => {
    switch (role) {
      case 'OWNER':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'MANAGER':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'SERVICE_WRITER':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'TECHNICIAN':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const rolePermissions: Record<string, string[]> = {
    OWNER: [
      'Full shop management access (same as Manager)',
      'Cannot be removed unless another Owner exists',
      'Manage team members, roles, and billing',
    ],
    MANAGER: [
      'Full shop management access',
      'Manage team members and roles',
      'Edit shop profile and settings',
      'Manage technicians and shop floor',
      'Create, edit, and delete tickets',
      'Run optimizer and view analytics',
      'Import/export data',
    ],
    SERVICE_WRITER: [
      'Create and manage tickets',
      'Assign jobs to technicians',
      'View shop profile (read-only)',
      'Run optimizer',
      'View analytics and reports',
      'Import ticket data',
    ],
    TECHNICIAN: [
      'View assigned tickets',
      'Control ticket timers',
      'Update ticket status',
      'View own schedule',
      'Limited read-only access',
    ],
  };

  if (!isManager) {
    return (
      <div className="min-h-screen bg-background">
        <TopBar />
        <div className="container mx-auto p-6">
          <Card>
            <CardHeader>
              <CardTitle>Access Denied</CardTitle>
              <CardDescription>
                Only managers can access team management
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-background">
      <div className="tech-grid" />
      <div className="relative z-10">
      <TopBar />
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className={cn(
          "flex gap-4",
          formFactor === 'desktop' 
            ? "flex-row items-center justify-between" 
            : "flex-col"
        )}>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Users className="h-8 w-8" />
              Team Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your shop's team members and their roles
            </p>
          </div>
          <div className={cn(
            "flex flex-wrap gap-2",
            formFactor !== 'desktop' && "w-full"
          )}>
            <Button variant="outline" onClick={() => setShowRoleGuide(true)}>
              <Info className="h-4 w-4 mr-2" />
              Role Guide
            </Button>
            <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
              <DialogTrigger asChild>
                <Button data-testid="invite-member-button">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
              </DialogTrigger>
              {/* PTP-013: the invite dialog is taller than a phone viewport once
                  Access and all five work roles are listed, and on WebKit phone
                  it did NOT scroll its own content — the lower controls simply
                  could not be reached. Cap it to the viewport and let it scroll,
                  which is what the other dialogs in this product already do. */}
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Invite Team Member</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join your shop team
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="colleague@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                  {/* ── ACCESS ───────────────────────────────────────────────
                      This used to be a single "Role" select offering Owner /
                      Manager / Service Writer / Technician — one control mixing
                      two unrelated questions, which is the exact conflation the
                      multi-role model exists to remove. Picking "Technician"
                      there was how an operator caused a technician row to
                      exist, so the legacy compatibility scalar had become a
                      product concept people were asked to reason about.

                      It is now two questions. This one is ADMINISTRATIVE
                      ACCESS, and Staff is the ordinary answer for shop floor
                      staffing: it confers no administration at all. Only
                      authorities this actor may actually confer are listed —
                      a manager sees Staff alone, because minting a manager or
                      an owner needs `manage_authority`, which they do not
                      hold. The server refuses regardless of what is sent. */}
                  <div className="space-y-2">
                    <Label htmlFor="invite-authority">Access</Label>
                    <p className="text-xs text-muted-foreground">
                      What administrative access should this person have?
                    </p>
                    <Select
                      value={inviteAuthority}
                      onValueChange={(value) => setInviteAuthority(value as AuthorityRole)}
                    >
                      <SelectTrigger id="invite-authority" data-testid="invite-authority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {invitableAuthorities.map((r) => (
                          <SelectItem key={r} value={r}>{AUTHORITY_LABELS[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ── WORK ROLES ───────────────────────────────────────────
                      What this person DOES. Independent of access, so
                      "Owner + Technician" and "Staff + Detail Specialist" are
                      both expressible and neither half implies the other. The
                      invitee may review this but cannot change it: the server
                      applies what the inviter chose. */}
                  <div className="space-y-2">
                    <WorkRoleMultiSelect
                      value={inviteWorkRoles}
                      onChange={setInviteWorkRoles}
                      label="Work roles"
                      description="What does this person do in the shop? Choose every job they will perform. Leaving it empty is valid for someone who does not work on vehicles."
                      idPrefix="invite-work-role"
                    />
                  </div>
                  {/* WS-ELIG B-E4 (CORE-W:45): name the existing roster entry so
                      acceptance adopts it (and its history) instead of creating a
                      second one. The person sees this before accepting. */}
                  {unclaimedRoster.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="invite-roster-entry">Already on your roster?</Label>
                      <Select value={inviteRosterEntry} onValueChange={setInviteRosterEntry}>
                        <SelectTrigger id="invite-roster-entry" data-testid="invite-roster-entry-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No — this is a new person</SelectItem>
                          {unclaimedRoster.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Choose their roster entry and, when they accept, it becomes theirs with the work already recorded
                        under it. They are not on the board until they accept.
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsInviteOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleInvite} data-testid="send-invite-button">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Team Members Table */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
            <CardDescription>
              Manage roles and access for {members.length} team member{members.length !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading team members...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No team members yet</div>
            ) : formFactor === 'phone' ? (
              /* ── PHONE: stacked cards, same data, same actions ───────────
                 Measured at 393px the six-column table laid out 758px wide and
                 put only Name and Email inside the fold. Access/Work Roles and
                 the "Edit roles" button — the two things team management IS —
                 sat off-screen behind a horizontal scrub, which is not a
                 practical shop-floor gesture.

                 This is a PRESENTATION switch and nothing more: every card
                 renders the same `describeMemberRoles` summary and calls the
                 same handlers as the table row below, so there is exactly one
                 mutation path. Tablet and desktop keep the table, which
                 measures usable at 768px and up. */
              <div className="space-y-3" data-testid="team-members-cards">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="rounded-lg border p-4 space-y-3"
                    data-testid={`team-member-card-${member.user_id}`}
                  >
                    <div className="space-y-1">
                      <p className="font-medium leading-tight break-words">
                        <span data-testid={`member-name-${member.user_id}`}>
                          {memberName(member) ?? <span className="italic text-muted-foreground">Name not set</span>}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground break-all">
                        {member.profiles?.email || 'N/A'}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                        {member.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Joined {new Date(member.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {renderRoleSummary(member)}
                    <div className="flex flex-wrap gap-2">
                      {renderMemberActions(member)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table data-testid="team-members-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        <span data-testid={`member-name-${member.user_id}`}>
                          {memberName(member) ?? <span className="italic text-muted-foreground">Name not set</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {member.profiles?.email || 'N/A'}
                      </TableCell>
                      {/* ── ACCESS + WORK ROLES ────────────────────────────
                          This used to be a single dropdown that turned one role
                          INTO another, so making an owner also a technician took
                          their access away, and a technician who also details was
                          not expressible at all. The row now SUMMARISES both and
                          opens an editor where each is changed independently. */}
                      <TableCell>{renderRoleSummary(member)}</TableCell>
                      <TableCell>
                        <Badge variant={member.status === 'active' ? 'default' : 'secondary'}>
                          {member.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(member.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">{renderMemberActions(member)}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* W3 (field test 2026-10-16): who can be scheduled — additive diagnostics +
            audited decisions; renders only for managers who hold manage_work_roles. */}
        {activeShop?.id && <WorkEligibilityReconciliationCard shopId={activeShop.id} />}

        {/* Edit Member Dialog */}
        <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
              <DialogDescription>
                The person's name as everyone in the shop sees it — the Front Office, the Dayboard and handoffs. Not an email address.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  data-testid="edit-member-name-input"
                  value={editName}
                  onChange={(e) => { setEditName(e.target.value); setEditNameError(null); }}
                  placeholder="First and last name"
                  aria-invalid={!!editNameError}
                />
                {editNameError && (
                  <p className="text-xs text-destructive" data-testid="edit-member-name-error">{editNameError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email Address</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Enter email"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingMember(null)}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} data-testid="edit-member-save">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Role Guide Dialog */}
        <Dialog open={showRoleGuide} onOpenChange={setShowRoleGuide}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Role & Permissions Guide</DialogTitle>
              <DialogDescription>
                Understanding team member roles and their capabilities
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {Object.entries(rolePermissions).map(([role, permissions]) => (
                <Card key={role}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Badge className={getRoleBadgeColor(role as AppRole)}>
                        {role.replace('_', ' ')}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {permissions.map((permission, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-primary mt-1">✓</span>
                          <span className="text-sm">{permission}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!memberToDelete} onOpenChange={() => setMemberToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
              <AlertDialogDescription>
                This will revoke their access to the shop. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Remove Member
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* One editor, mounted once. Full-screen on a phone, dialog from sm up. */}
        <MemberRolesEditor
          open={!!editingRolesFor}
          onOpenChange={(o) => !o && setEditingRolesFor(null)}
          saving={savingRoles}
          actor={
            membership
              ? {
                  userId: membership.user_id,
                  authority: resolveMembershipRoles(membership as never).authority,
                }
              : null
          }
          member={(() => {
            const m = members.find((x) => x.id === editingRolesFor);
            if (!m) return null;
            return {
              userId: m.user_id,
              displayName: memberName(m) ?? 'This member',
              authority: resolveMembershipRoles({
                role: m.role,
                authority_role: (m as unknown as { authority_role?: string }).authority_role,
                work_roles: workRolesByMembership[m.id] ?? null,
              }).authority,
              workRoles: workRolesByMembership[m.id] ?? [],
            };
          })()}
          onSave={async (next) => {
            const m = members.find((x) => x.id === editingRolesFor);
            if (m) await handleSaveRoles(m, next);
          }}
        />

        {/* Check Email Dialog for Tutorial */}
        <CheckEmailDialog />
      </div>
      </div>
    </div>
  );
};

export default TeamManagement;
